import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Skeleton, message } from "antd";
import SearchBar from "../../Elements/Search/SearchBar";
import TextStyles from "../../Elements/TextStyles/TextStyles";
import { ReactComponent as BackIcon } from "../../../assets/svgs/back-button.svg";
import { getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import { QuestionFormScope1234 } from "../../../pages/QuestionsBank/components/questionFormScope1234";

const FormDetailsPage = () => {
  const [sections, setSections] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [prefilledAnswers, setPrefilledAnswers] = useState([]);
  const [flatAnswers, setFlatAnswers] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [selectedSubsectionName, setSelectedSubsectionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { formId } = useParams();
  
  const pageTitle = location.state?.title || location.state?.formTitle || "Form Details";
  const projectName = location.state?.projectName || "N/A";
  const parentState = location.state?.parentState || location.state || {};
  
  const getId = (val) => (typeof val === "object" ? val?._id : val);
  
  const resolvedAllotmentId = getId(location.state?.allotmentId) || getId(location.state?.standardFormAllotmentId) || getId(location.state?.id);
  const resolvedProjectId = getId(location.state?.projectId) || getId(parentState?.projectId);
  const resolvedUserId = getId(parentState?.userId) || getId(parentState?.employeeId);

  const fetchAnswers = useCallback(async (section, subsection) => {
    if ((!resolvedAllotmentId && !resolvedProjectId) || !section || !subsection || !resolvedUserId) return { dataArr: [], combinedMap: {} };

    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_PROJECT_ANSWERS(
          resolvedAllotmentId,
          section,
          subsection,
          resolvedUserId,
          resolvedProjectId
        ),
      });

      if (response.success && response.data) {
        const combinedMap = {};
        const dataArr = Array.isArray(response.data) ? response.data : [response.data];

        const localFlatten = (list, prefix = "", branchId = "") => {
          if (!list || !Array.isArray(list)) return;

          list.forEach((ans) => {
            const bId = ans.questionId || ans.questionID;
            if (!bId) return;

            // 1. Calculate the value once
            const newVal = ans.value !== undefined ? ans.value : ans.answer;

            // Construct hierarchical key: base + tripPrefix + month + branch
            // Restore to original logic to ensure stability
            let mId = `${bId}${prefix}`;
            if (ans.month) mId += `_${ans.month}`;
            if (branchId) {
              mId += (ans.month ? `:::${branchId}` : `_${branchId}`);
            }

            // 0. Auxiliary Suffix Mapping (Ensures branching prefill works without breaking base keys)
            if (ans.optionValue && newVal !== undefined) {
               const suffixedKey = `${bId}${prefix}_${ans.optionValue}${ans.month ? `_${ans.month}` : ""}`;
               if (combinedMap[suffixedKey] === undefined || combinedMap[suffixedKey] === "") {
                  combinedMap[suffixedKey] = newVal;
               }
            }

            // 1. Handle Table Data
            if (ans.questionType === "table" || (Array.isArray(ans.tableRows) && ans.tableRows.length > 0)) {
              // Aggressively extract all fields from all rows into suffixed keys for pre-selection
              if (ans.tableRows && Array.isArray(ans.tableRows)) {
                ans.tableRows.forEach((row) => {
                  if (row.values) {
                    Object.entries(row.values).forEach(([fieldId, fieldVal]) => {
                      const baseKey = `${bId}${prefix}_${fieldId}`;
                      const suffix = `${ans.month ? `_${ans.month}` : ""}${branchId ? (ans.month ? `:::${branchId}` : `_${branchId}`) : ""}`;
                      const prefixedKey = `${baseKey}${suffix}`;
                      
                      [prefixedKey, fieldId].forEach((k) => {
                        if (combinedMap[k] === undefined) {
                          combinedMap[k] = fieldVal;
                        } else {
                          const current = Array.isArray(combinedMap[k]) ? combinedMap[k] : [combinedMap[k]];
                          const next = Array.isArray(fieldVal) ? fieldVal : [fieldVal];
                          combinedMap[k] = Array.from(new Set([...current, ...next]));
                        }
                      });

                      // 1. Expansion for Key-Value arrays (e.g., gas_quantity: [{key: 'co2', value: 10}])
                      if (Array.isArray(fieldVal)) {
                        fieldVal.forEach(kv => {
                          if (kv && typeof kv === 'object' && kv.key && kv.value !== undefined) {
                            const kvKey = `${baseKey}_${kv.key}${suffix}`;
                            combinedMap[kvKey] = kv.value;
                          }
                        });
                      }

                      // 2. Expansion for Repeater/Details arrays (e.g., details: [{item_type: '...'}])
                      if (Array.isArray(fieldVal) && fieldVal.length > 0 && typeof fieldVal[0] === 'object' && !fieldVal[0].key) {
                        fieldVal.forEach((innerObj, loopIdx) => {
                          Object.entries(innerObj).forEach(([innerId, innerVal]) => {
                            const loopBaseKey = `${bId}${prefix}_${fieldId}_${innerId}_loop_${loopIdx}`;
                            const loopKey = `${loopBaseKey}${suffix}`;
                            combinedMap[loopKey] = innerVal;
                            
                            // Also set a version without the fieldId in the middle for easier lookup
                            const simpleLoopBaseKey = `${bId}${prefix}_${innerId}_loop_${loopIdx}`;
                            combinedMap[`${simpleLoopBaseKey}${suffix}`] = innerVal;
                          });
                        });
                      }
                    });
                  }
                });
              }

              const tableData = {};
              ans.tableRows.forEach((row) => {
                const enhancedValues = { ...row.values };
                let itemType = "default";
                if (row.values?.item_type) {
                  itemType = Array.isArray(row.values.item_type) ? row.values.item_type[0] : row.values.item_type;
                } else {
                  // Aggressive search for item_type in nested details (Purchased Goods etc.)
                  Object.values(row.values || {}).some(val => {
                    if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
                      if (val[0].item_type) {
                        const it = val[0].item_type;
                        itemType = Array.isArray(it) ? it[0] : it;
                        return true;
                      }
                    }
                    return false;
                  });
                }

                // Flatten nested repeaters inside row (e.g. number_of_centralized_ac_details)
                Object.entries(row.values || {}).forEach(([fId, fVal]) => {
                  if (Array.isArray(fVal) && fVal.length > 0 && typeof fVal[0] === "object") {
                    fVal.forEach((innerObj, loopIdx) => {
                      // Support {key, value} expansion (e.g., gas_quantity: [{key: 'co2', value: 2}])
                      if (innerObj && typeof innerObj === 'object' && innerObj.key && innerObj.value !== undefined) {
                        enhancedValues[`${fId}_${innerObj.key}`] = innerObj.value;
                      }

                      Object.entries(innerObj).forEach(([innerId, innerVal]) => {
                        enhancedValues[`${innerId}_loop_${loopIdx}`] = innerVal;
                      });
                    });
                  }
                });

                if (!tableData[itemType]) tableData[itemType] = [];
                tableData[itemType].push({ ...row, values: enhancedValues });
              });

              // Merge tables across documents
              if (combinedMap[mId]) {
                Object.keys(tableData).forEach((cat) => {
                  if (combinedMap[mId][cat]) {
                    combinedMap[mId][cat] = [...combinedMap[mId][cat], ...tableData[cat]];
                  } else {
                    combinedMap[mId][cat] = tableData[cat];
                  }
                });
              } else {
                combinedMap[mId] = tableData;
              }
            }
            // 2. Handle Business Travel (Special Structure - supports month suffixes like _jan)
            else if (bId.startsWith("number_of_trip_") && (ans.value !== undefined || ans.details)) {
              const detailsKey = `${bId}_details`;
              const details = ans.value?.details || ans.details || ans[detailsKey];
              const tripCount = ans.value?.number_of_trip !== undefined ? ans.value.number_of_trip : (ans.number_of_trip !== undefined ? ans.number_of_trip : (ans.value !== undefined ? ans.value : 0));

              combinedMap[mId] = tripCount;

              const isIndividual = bId.includes("_individual");
              const branchId = isIndividual ? "individual_business_travel" : "group_business_travel";

              if (Array.isArray(details)) {
                details.forEach((tripObj, idx) => {
                  const tripLabel = `${bId}_${idx + 1}`;
                  const tripAnswers = tripObj[tripLabel];
                  if (Array.isArray(tripAnswers)) {
                    // Pass branchId separately for better hierarchical key construction
                    localFlatten(tripAnswers, `_trip_${idx}`, branchId);
                  }
                });
              }
            }
            // 3. Standard Values
            else {
              if (newVal === undefined) return;

              // Expansion for Key-Value arrays (e.g., climate_related_issues: [{key: 'board_chair', value: '...' }])
              if (Array.isArray(newVal)) {
                newVal.forEach(kv => {
                  if (kv && typeof kv === 'object' && kv.key && kv.value !== undefined) {
                    const kvKey = `${mId}_${kv.key}`;
                    combinedMap[kvKey] = kv.value;
                  }
                });
              }

              const qType = ans.questionType || ans.type || "";
              const isMultiselect = qType === "multiselect";

              if (isMultiselect) {
                const current = combinedMap[mId]
                  ? (Array.isArray(combinedMap[mId]) ? combinedMap[mId] : [combinedMap[mId]])
                  : [];
                const next = Array.isArray(newVal) ? newVal : (newVal !== undefined ? [newVal] : []);
                combinedMap[mId] = Array.from(new Set([...current, ...next]));
              } else {
                if (newVal !== undefined && newVal !== "" && !Array.isArray(newVal)) {
                  if (combinedMap[mId] === undefined || combinedMap[mId] === "" || combinedMap[mId] === null || Array.isArray(combinedMap[mId])) {
                    combinedMap[mId] = newVal;
                  }
                }
              }
            }
          });
        };

        // Flatten all documents into combinedMap
        dataArr.forEach((item) => {
          if (item.answers && Array.isArray(item.answers)) {
            localFlatten(item.answers);
          }
        });

        console.log("[FormDetailsPage] FINAL COMBINED MAP:", Object.keys(combinedMap));

        setFlatAnswers(combinedMap);
        setPrefilledAnswers(dataArr.reduce((acc, curr) => [...acc, ...(curr.answers || [])], []));
        return { dataArr, combinedMap };
      }
      return { dataArr: [], combinedMap: {} };
    } catch (error) {
      console.error("Error fetching answers", error);
      return { dataArr: [], combinedMap: {} };
    }
  }, [resolvedAllotmentId, resolvedProjectId, resolvedUserId]);

  const fetchQuestions = useCallback(async (currentFormId, sectionId, subId, answersInfo) => {
    const { combinedMap } = answersInfo || { combinedMap: {} };
    if (!currentFormId || !sectionId || !subId) return;
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_USER_DETAIL_STANDARD_QUESTIONS(currentFormId, sectionId, subId),
      });

      if (response.success) {
        let divisions = response.data?.divisions || [];
        if (divisions.length === 0 && response.data?.questions) {
          divisions = [{ questions: response.data.questions }];
        } else if (divisions.length === 0 && Array.isArray(response.data)) {
          divisions = [{ questions: response.data }];
        }

        const enhancedDivisions = divisions.map((div) => ({
          ...div,
          questions: (div.questions || []).map((q) => {
            const qId = (q.questionId || q.questionID || "").trim();
            const savedValue = combinedMap?.[qId];

            if (savedValue !== undefined) {
              return {
                ...q,
                value: savedValue,
                answer: savedValue,
              };
            }
            return q;
          }),
        }));

        setQuestions(enhancedDivisions);
      }
    } catch (error) {
      console.error("Failed to load questions", error);
      setQuestions([]);
    }
  }, []);

  const fetchSubsections = useCallback(async (currentFormId, sectionId) => {
    if (!currentFormId || !sectionId) return;
    setLoading(true);
    try {
      const res = await getApi({
        apiUrl: API_URLS.GET_USER_DETAIL_STANDARD_SUBSECTIONS(currentFormId, sectionId),
      });

      if (res?.success) {
        const subsectionList = res.data?.subsections || (Array.isArray(res.data) ? res.data : []);
        setSubsections(subsectionList);

        if (subsectionList.length > 0) {
          const firstSub = subsectionList[0];
          const subId = firstSub.id || firstSub.subsectionId;
          setSelectedSubsection(subId);
          setSelectedSubsectionName(firstSub.name || "");
          
          const answersInfo = await fetchAnswers(sectionId, subId);
          await fetchQuestions(currentFormId, sectionId, subId, answersInfo);
        } else {
          setSelectedSubsection(null);
          setSelectedSubsectionName("");
          setQuestions([]);
          setPrefilledAnswers([]);
        }
      } else {
        setSubsections([]);
        setQuestions([]);
        setPrefilledAnswers([]);
      }
    } catch (error) {
      console.error("Failed to load subsections", error);
      message.error("Failed to load subsections");
      setSubsections([]);
      setQuestions([]);
      setPrefilledAnswers([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAnswers, fetchQuestions]);

  const fetchSections = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    try {
      const res = await getApi({
        apiUrl: API_URLS.GET_USER_DETAIL_STANDARD_SECTIONS(formId),
      });

      if (res?.success) {
        const allSections = res.data?.sections || (Array.isArray(res.data) ? res.data : []);
        setSections(allSections);

        if (allSections.length > 0) {
          const first = allSections[0];
          const firstId = first.sectionId || first.id || first.section;
          setSelectedSection(firstId);
          await fetchSubsections(formId, firstId);
        } else {
          setSelectedSection(null);
          setSubsections([]);
          setQuestions([]);
        }
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Failed to load sections", error);
      message.error("Failed to load sections");
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [fetchSubsections, formId]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  return (
    <div className="min-h-screen px-[20px] pb-20 ">
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>
        <p>Users</p>
        <span>/</span>
        <TextStyles color="gradient">{pageTitle}</TextStyles>
      </div>

      <div className="flex items-center gap-2 text-xl font-bold mb-5">
        <BackIcon
          onClick={() =>
            navigate(location.state?.from || -1, {
              state: location.state,
            })
          }
          className="cursor-pointer"
        />
        <div>
          <TextStyles color="gradient">{pageTitle}</TextStyles>
          <p className="text-sm font-normal text-gray-500 mt-1">
            Project: {projectName}
          </p>
        </div>
      </div>

      <div
        style={{
          borderRadius: "10px",
          paddingTop: "20px",
          paddingBottom: "20px",
          boxShadow: "0px 0px 16px 0px #3BA2B91F",
          backgroundColor: "#fff",
        }}
      >
        <div className="w-full shadow-[0_0_16px_0_#3BA2B91F] mb-6">
          <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
            {sections.length > 0 ? (
              sections.map((section) => {
                const sectionKey = section.sectionId || section.id || section.section;
                return (
                  <div
                    key={sectionKey}
                    className={`relative h-10 py-6 px-2 flex items-center justify-center text-[13px] font-medium cursor-pointer rounded-xl mx-1 my-1 transition-all duration-200 ease-in-out border border-[#3BA2B959] ${
                      selectedSection === sectionKey
                        ? "bg-[#06513D] text-white shadow-md transform scale-105"
                        : "bg-transparent text-gray-700 hover:text-gray-900"
                    }`}
                    onClick={() => {
                      setSelectedSection(sectionKey);
                      setSelectedSubsection(null);
                      setSelectedSubsectionName("");
                      setQuestions([]);
                      setPrefilledAnswers([]);
                      fetchSubsections(formId, sectionKey);
                    }}
                  >
                    <span className="text-center leading-tight capitalize">
                      {section?.name?.toLowerCase() || sectionKey}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 p-4">No Sections available</p>
            )}
          </div>

          {selectedSection && (
            <div className="flex flex-wrap gap-5 p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
              {subsections.length > 0 ? (
                subsections.map((sub) => {
                  const subsectionId = sub.id || sub.subsectionId;
                  const isSelected = selectedSubsection === subsectionId;

                  return (
                    <div
                      key={subsectionId}
                      className={`capitalize p-3 rounded-xl cursor-pointer border transition-all duration-200 ${
                        isSelected
                          ? "bg-[#E6EEEC] shadow-md"
                          : "bg-transparent text-black hover:bg-gray-100"
                      }`}
                      onClick={async () => {
                        setSelectedSubsection(subsectionId);
                        setSelectedSubsectionName(sub.name || "");
                        setPrefilledAnswers([]);
                        setFlatAnswers({});
                        const answersInfo = await fetchAnswers(selectedSection, subsectionId);
                        await fetchQuestions(formId, selectedSection, subsectionId, answersInfo);
                      }}
                    >
                      {sub.name}
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 p-2">No subsections available</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center px-8 border-b border-gray-100 pb-5 flex-wrap gap-4">
          <div className="flex gap-4 flex-wrap items-center">
            <p className="px-8 h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-sm font-medium">
              TOTAL QUESTIONS: {questions.length}
            </p>
            <SearchBar width="300px" onSearch={(value) => setSearchTerm(value)} />
          </div>
        </div>

        <div className="px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-teal-100 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-400 font-bold uppercase tracking-widest text-xs">Loading...</p>
              <div className="w-full mt-8">
                <Skeleton active />
                <Skeleton active />
              </div>
            </div>
          ) : questions.length > 0 ? (
            <div className="py-6">
              <QuestionFormScope1234
                key={`${selectedSection}_${selectedSubsection}`}
                questions={questions}
                sectionId={selectedSection}
                subSectionId={selectedSubsection}
                searchTerm={searchTerm}
                flatAnswers={{ ...flatAnswers }}
                prefilledAnswers={prefilledAnswers}
              />
            </div>
          ) : (
            <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-2xl mx-2">
              <p className="text-gray-400 font-medium italic">No questions found in this sub-section.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormDetailsPage;
